import {
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SxProps,
  Typography,
} from "@mui/material"
import {
  Chart,
  Chart as ChartJS,
  ChartOptions,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
} from "chart.js"
import { useEffect, useMemo, useRef, useState } from "react"
import { Scatter } from "react-chartjs-2"
import zoomPlugin from "chartjs-plugin-zoom"
import { NodeResult, RunDto, RunResults } from "@custom-types/modelRun"
import Button from "@mui/material/Button"
import DownloadChartImageDialog from "@features/results/DownloadChartImageDialog"
import { areResultsOfTheSameUnitFormat, getRunNamesMap } from "@utils/run"
import { GroupNodeLabel } from "@features/graph/graphViewModel"
import { formatValuePythonic } from "@utils/format/pythonicFormat"
import { color_tones, to_rgba } from "@features/results/Result/const"
import { generateChartLabel } from "@utils/chartLabelUtils"

ChartJS.register(LinearScale, PointElement, LineElement, Tooltip, Legend, zoomPlugin)

type ScatterPlotProps = {
  rawResultDataFilterNodes: RunResults
  lagShift: number
  node: GroupNodeLabel | null
  runs: RunDto[]
  onlyChart?: boolean
  maintainAspectRatio?: boolean
  chartContainerSx?: SxProps
  sx?: SxProps
  legendPosition?: "top" | "bottom"
  showLegend?: boolean
  hiddenDatasets?: string[]
  onLegendClick?: (datasetLabel: string) => void
}

export const ScatterPlot = ({
  rawResultDataFilterNodes,
  lagShift,
  node,
  runs,
  onlyChart = false,
  maintainAspectRatio = false,
  chartContainerSx = { maxHeight: "500px", mb: 3 },
  sx = { my: 2 },
  legendPosition = "top",
  showLegend = true,
  hiddenDatasets = [],
  onLegendClick,
}: ScatterPlotProps) => {
  const [labels, setLabels] = useState<Set<number>>(new Set())
  const [selectedTimeSteps, setSelectedTimeSteps] = useState<number[]>([])
  const [maxXRange, setMaxXRange] = useState(0)
  const chartRef = useRef<Chart<"scatter", unknown[], unknown>>()
  const chartContainerRef = useRef<HTMLDivElement | null>(null)

  const runNamesMap = useMemo(() => getRunNamesMap(runs), [runs])
  const runIds = runs.map((run) => run.key)

  const scatterChartOptons: ChartOptions<"scatter"> = {
    responsive: true,
    maintainAspectRatio,
    scales: {
      x: {
        min: 0,
        max: maxXRange,
      },
      y: {
        ticks: {
          callback: function (value: number | string) {
            return formatValuePythonic(value, node?.format)
          },
        },
      },
    },
    plugins: {
      legend: {
        position: legendPosition,
        display: showLegend,
        onClick: onLegendClick
          ? function (
              _event: unknown,
              legendItem: { datasetIndex?: number },
              legend: { chart: { data: { datasets: { label?: string }[] } } },
            ) {
              const index = legendItem.datasetIndex
              if (index === undefined) return
              const dataset = legend.chart.data.datasets[index]
              const datasetLabel = dataset?.label
              if (datasetLabel) {
                onLegendClick(datasetLabel)
              }
            }
          : undefined,
      },
      zoom: {
        limits: {
          x: { minRange: 2, min: 0, max: maxXRange },
        },
        zoom: {
          drag: {
            enabled: true,
          },
          pinch: {
            enabled: true,
          },
          mode: "x",
        },
      },
    },
  }

  const nodeResults = useMemo(
    () =>
      rawResultDataFilterNodes
        ? Object.values(rawResultDataFilterNodes).reduce(
            (res, curr) => [...res, ...Object.values(curr)],
            [] as NodeResult[][],
          )
        : [],
    [rawResultDataFilterNodes],
  )

  const preparedData = useMemo(() => {
    const tempLabels: number[] = []
    // guarantee data is orderly
    const result = nodeResults.reduce((acc, item) => {
      const sorted = Object.values(item).sort((a, b) => (a.timestep > b.timestep ? 1 : -1))
      sorted.forEach((el, index) => {
        if (index >= lagShift) {
          acc[el.key] = {
            ...acc[el.key],
            [el.timestep]: el.data,
          }

          tempLabels.push(el.timestep)
        }
      })

      return acc
    }, {} as { [key: string]: { [key: string]: number[] } })

    const uniqueLabels = [...new Set(tempLabels)].sort((a, b) => a - b)
    setLabels(new Set(uniqueLabels))
    setSelectedTimeSteps((prev) => {
      if (prev.length > 0 && uniqueLabels.includes(prev[0]!)) return prev
      return uniqueLabels.length > 0 ? [uniqueLabels[0]!] : []
    })
    return result
  }, [lagShift, nodeResults])

  const datasets = useMemo(() => {
    const tempDatasets: {
      label: string
      backgroundColor: string
      borderColor: string
      data: { x: string; y: number }[]
      hidden?: boolean
    }[] = []
    let colorIndex = 0
    const numberOfRuns = runs.length

    // eslint-disable-next-line guard-for-in -- FIX: check for unwanted properties or refactor to for..of
    for (const key in preparedData) {
      const data: { x: string; y: number }[] = []
      // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
      Object.keys(preparedData[key]).forEach((year) => {
        // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
        preparedData[key][year].forEach((item, index) => {
          selectedTimeSteps.includes(+year) && data.push({ x: `${index}`, y: item })
        })
      })

      const color = color_tones[colorIndex % color_tones.length] ?? color_tones[0] ?? [0, 0, 0, 1]
      colorIndex += 1

      const currentRun = runs.find((run) => run.key === key)
      const runName = currentRun ? runNamesMap[currentRun.key] : undefined
      const runVersion = currentRun?.version

      const label = generateChartLabel({
        nodeName: node?.label ?? "",
        nodeUnit: node?.format,
        runName,
        runVersion,
        numberOfRuns,
      })

      tempDatasets.push({
        label,
        backgroundColor: to_rgba(color),
        borderColor: to_rgba(color),
        data,
      })
    }

    return tempDatasets
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIX: deps after bulk migration
  }, [preparedData, runNamesMap, selectedTimeSteps, runs, node])

  useEffect(() => {
    let maxXindex = 0
    // eslint-disable-next-line guard-for-in -- FIX: check for unwanted properties or refactor to for..of
    for (const key in preparedData) {
      // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
      Object.keys(preparedData[key]).forEach((year) => {
        maxXindex =
          // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
          maxXindex < preparedData[key][year].length ? preparedData[key][year].length : maxXindex
      })
    }
    setMaxXRange(maxXindex)
  }, [preparedData])

  const chartData = useMemo(
    () => ({
      labels: [...Array(maxXRange).keys()],
      datasets: datasets.map((dataset) => ({
        ...dataset,
        hidden: dataset.label ? hiddenDatasets.includes(dataset.label) : false,
      })),
    }),
    [datasets, hiddenDatasets, maxXRange],
  )

  const areTheSameUnitsUsed = areResultsOfTheSameUnitFormat(rawResultDataFilterNodes)
  if (!areTheSameUnitsUsed) {
    return (
      <Typography>
        Can not render <strong>data scatter chart</strong> because selected node(s) are using
        different units.
      </Typography>
    )
  }

  const handleResetZoom = () => {
    if (chartRef && chartRef.current) {
      chartRef.current.resetZoom()
    }
  }

  return (
    <Grid container sx={sx} alignItems="center">
      <Grid item xs={3} sx={{ my: 1 }}>
        <FormControl fullWidth>
          <InputLabel id="agg-label">Selected timesteps</InputLabel>
          <Select
            labelId="agg-label"
            value={selectedTimeSteps}
            disabled={labels.size === 0}
            label="Selected timesteps"
            onChange={(value) => setSelectedTimeSteps([+value.target.value])}
            size={"small"}
          >
            <MenuItem value={""}>&nbsp;</MenuItem>
            {Array.from(labels).map((item) => (
              <MenuItem key={item} value={item}>
                {item}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      {!onlyChart && (
        <Grid item xs="auto" sx={{ mb: 2, mt: 1 }}>
          <Button onClick={handleResetZoom} variant={"contained"} sx={{ ml: 2 }}>
            Reset zoom
          </Button>
        </Grid>
      )}
      {!onlyChart && (
        <Grid item>
          <DownloadChartImageDialog
            buttonProps={{ sx: { ml: 1 } }}
            chartRef={chartContainerRef}
            defaultFileName="Scatter Plot"
            runReference={runIds.join(" vs. ")}
          />
        </Grid>
      )}
      <Grid item xs={12} ref={chartContainerRef} sx={sx}>
        <Box sx={chartContainerSx}>
          {!onlyChart && (
            <Typography variant="h6" textAlign="center" sx={{ mb: 1 }}>
              Data scatter for <b>{node?.label || ""}</b>{" "}
            </Typography>
          )}
          <Scatter
            options={scatterChartOptons}
            data={chartData}
            ref={chartRef}
            height={onlyChart ? undefined : 500}
          />
        </Box>
      </Grid>
    </Grid>
  )
}
